/* Family-composition signup step — shown right after the inscription step for
 * registering subject roles when the study has auto-creatable DATA-ONLY roles
 * (e.g. Enfant) and/or roles this registrant may declare absent (e.g. Père,
 * declarable by Mère): the first family member declares "how many?" per
 * data-only role, bounded by the researcher-configured min/max, and answers one
 * Oui/Non question per absence-declarable role, both with the researcher-written
 * wording. OBLIGATORY when shown (no skip path). Absence questions render FIRST
 * (explicit JSX order — never sort on the synthetic question `order`).
 *
 * SKIP LOGIC: on mount the server pre-check returns, for the entered numéro de
 * dossier, the counts of existing members per data-only role and — per role the
 * caller may declare absent — whether a member is already waited on (registered
 * or holding a grant) and whether an active absence declaration exists. Roles
 * already populated / declared are not asked; a role missing from the answer IS
 * asked; when NOTHING remains (neither counts nor absences), the step
 * auto-completes without rendering. The pre-check is fail-soft: on error every
 * question is shown (the server dedupes and validates at consent).
 *
 * PERSISTENCE: like every signup value, nothing is sent here — the declarations
 * are handed up to HomeContent (pendingFamilyComposition / pendingRoleAbsences)
 * and ride the consent body; the rows are written server-side at consent time.
 */
import { ReactElement, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import Container from "../../../atoms/Container";
import { FieldError } from "../../../atoms/LoginTextField";
import NextPrevButtons from "../../../molecules/NextPrevButtons";
import Question from "../../../molecules/Question/Question";
import ResonanceLoader from "../../../atoms/ResonanceLoader/ResonanceLoader";
import useVocalisation from "../../../../hooks/useVocalisation";
import { useConfig } from "../../../../contexts/ConfigContext";
import { useHomeContext } from "../../../../pages/Home/HomeContent";
import {
  FamilyPrecheckAbsenceRole,
  postFamilyPrecheck,
} from "../../../../services/user.service";
import { ProfileType } from "../../../../types/profile";
import {
  NumberQuestionType,
  NumberSeparatorEnum,
  QuestionTypeEnum,
  RadioQuestionType,
} from "../../../../types/Question";

export interface FamilyCompositionDeclaration {
  study_role_id: number;
  count: number;
}

// One answer per absence-declarable role: the answer that declares the absence (Non by
// default, Oui when the role says so — see absentAnswerOf) ⇒ is_absent (declaration
// written); the other one ⇒ the role exists in the dossier (a stale declaration is lifted
// server-side).
export interface RoleAbsenceDeclaration {
  study_role_id: number;
  is_absent: boolean;
}

interface DisplayFamilyCompositionProps {
  // The data-only roles to declare (helper/familyComposition.ts).
  targetRoles: ProfileType[];
  // The roles this registrant may declare absent (helper/familyComposition.ts).
  absenceRoles: ProfileType[];
  // The numéro de dossier entered on the inscription step (skip pre-check key).
  externalId: string | null;
  // Back-navigation prefill.
  initialDeclarations: FamilyCompositionDeclaration[] | null;
  initialAbsences: RoleAbsenceDeclaration[] | null;
  onCompleted: (
    declarations: FamilyCompositionDeclaration[],
    absences: RoleAbsenceDeclaration[]
  ) => void;
}

const roleBounds = (role: ProfileType): { min: number; max: number } => ({
  min: role.attributes?.auto_create_min ?? 1,
  max: role.attributes?.auto_create_max ?? role.attributes?.auto_create_min ?? 1,
});

// Rendered through the real questionnaire Question component (NUMBER type) so the
// field shares the same chrome and the tel keyboard as any other number question.
const buildCountQuestion = (role: ProfileType, fallbackTitle: string): NumberQuestionType => {
  const { min, max } = roleBounds(role);
  return {
    id: -(role.id ?? 0),
    title: role.attributes?.auto_create_question_label || fallbackTitle,
    description: "",
    export_code: `family_${role.id}`,
    integral: { is_integral: false, integral_errorcode: null },
    is_mandatory_question: true,
    is_active: true,
    score_coefficient: 0,
    order: 9997,
    version: "1",
    type: QuestionTypeEnum.NUMBER,
    detail: {
      min_value: min,
      max_value: max,
      number_decimal: 0,
      prefix: "",
      suffix: "",
      separator: NumberSeparatorEnum.DOT,
    },
  };
};

// Answer ids of the synthetic absence radio (never 0: AnswerRadio treats a falsy
// value as "nothing selected").
const ABSENCE_YES = 1;
const ABSENCE_NO = 2;
type AbsenceAnswer = typeof ABSENCE_YES | typeof ABSENCE_NO;

// The researcher words the question freely, so the answer meaning "nobody of this role"
// depends on it: Non for « Y a-t-il un second titulaire ? », Oui for « Je suis la seule
// titulaire » (study_role_configs.absence_declared_on_yes).
const absentAnswerOf = (role: ProfileType): AbsenceAnswer =>
  role.attributes?.absence_declared_on_yes ? ABSENCE_YES : ABSENCE_NO;

// Synthetic RADIOBUTTON question (precedent: DisplayStratQuestions toRadioQuestion) so
// the Oui/Non control is the exact questionnaire radio card. The id lives in its own
// negative namespace (count questions use -role.id); `order` is informational only —
// the step renders absence questions first by explicit JSX order.
const buildAbsenceQuestion = (
  role: ProfileType,
  fallbackTitle: string,
  yesLabel: string,
  noLabel: string
): RadioQuestionType => {
  const id = -1_000_000 - (role.id ?? 0);
  return {
    id,
    title: role.attributes?.absence_question_label || fallbackTitle,
    description: "",
    export_code: `absence_${role.id}`,
    integral: { is_integral: false, integral_errorcode: null },
    is_mandatory_question: true,
    is_active: true,
    score_coefficient: 0,
    order: 9996,
    version: "1",
    type: QuestionTypeEnum.RADIOBUTTON,
    detail: {
      id,
      version: 1,
      answers: [
        { id: ABSENCE_YES, label: yesLabel, order: 1 },
        { id: ABSENCE_NO, label: noLabel, order: 2 },
      ],
    },
  };
};

const DisplayFamilyComposition = ({
  targetRoles,
  absenceRoles,
  externalId,
  initialDeclarations,
  initialAbsences,
  onCompleted,
}: DisplayFamilyCompositionProps): ReactElement => {
  const { t } = useTranslation();
  const { readText } = useVocalisation();
  const { study } = useConfig();
  const { goPrevious } = useHomeContext();

  // null while the pre-check runs (loader), then the roles still to declare.
  const [visibleRoles, setVisibleRoles] = useState<ProfileType[] | null>(null);
  // The absence-declarable roles still to ask (set together with visibleRoles).
  const [visibleAbsenceRoles, setVisibleAbsenceRoles] = useState<ProfileType[]>([]);
  const [values, setValues] = useState<Record<number, string>>(() => {
    const seeded: Record<number, string> = {};
    (initialDeclarations ?? []).forEach((declaration) => {
      seeded[declaration.study_role_id] = String(declaration.count);
    });
    return seeded;
  });
  // Absence answers live in their own numeric map (1 = Oui, 2 = Non), never in the
  // string `values` map of the count questions.
  const [absenceValues, setAbsenceValues] = useState<
    Record<number, AbsenceAnswer | undefined>
  >(() => {
    const seeded: Record<number, AbsenceAnswer | undefined> = {};
    (initialAbsences ?? []).forEach((absence) => {
      const role = absenceRoles.find((candidate) => candidate.id === absence.study_role_id);
      const absentAnswer = role ? absentAnswerOf(role) : ABSENCE_NO;
      const presentAnswer = absentAnswer === ABSENCE_YES ? ABSENCE_NO : ABSENCE_YES;
      seeded[absence.study_role_id] = absence.is_absent ? absentAnswer : presentAnswer;
    });
    return seeded;
  });
  const [showErrors, setShowErrors] = useState(false);
  const completedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const resolveVisibleRoles = async (): Promise<void> => {
      let counts: Record<number, number> = {};
      // undefined = the pre-check failed (fail OPEN: every absence question is asked,
      // like the counts); [] = nothing known (an older API without the key) ⇒ asked too.
      let absenceRolesFromPrecheck: FamilyPrecheckAbsenceRole[] | undefined = [];
      if (externalId) {
        try {
          const { data } = await postFamilyPrecheck(
            { studyId: study.id },
            { external_id: externalId }
          );
          counts = data?.counts ?? {};
          absenceRolesFromPrecheck = data?.absence_roles ?? [];
        } catch {
          // Fail-soft: show every role — the server's zero-members guard dedupes at consent.
          counts = {};
          absenceRolesFromPrecheck = undefined;
        }
      }
      if (cancelled) return;
      const remainingCounts = targetRoles.filter(
        (role) => (counts[role.id ?? -1] ?? 0) === 0
      );
      // An absence declaration is dossier-keyed: without a numéro there is nothing to
      // declare. A role already waited on (registered / granted member) or already
      // declared absent is not asked; a role missing from the answer IS asked.
      const remainingAbsences = externalId
        ? absenceRoles.filter((role) => {
            const answer = absenceRolesFromPrecheck?.find(
              (entry) => entry.study_role_id === role.id
            );
            return !answer?.has_member && !answer?.declared_absent;
          })
        : [];
      if (!remainingCounts.length && !remainingAbsences.length) {
        // Family already declared (other parent / investigator) — nothing to ask.
        if (!completedRef.current) {
          completedRef.current = true;
          onCompleted([], []);
        }
        return;
      }
      setVisibleAbsenceRoles(remainingAbsences);
      setVisibleRoles(remainingCounts);
    };
    resolveVisibleRoles();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (visibleRoles === null) return <ResonanceLoader />;

  const errorForRole = (role: ProfileType): string | null => {
    const { min, max } = roleBounds(role);
    const raw = (values[role.id ?? -1] ?? "").trim();
    if (!raw) return t("form_error_required");
    const parsed = Number(raw);
    if (!Number.isInteger(parsed) || parsed < min || parsed > max) {
      return t("answer_number_constraint_min_max", { min, max });
    }
    return null;
  };

  const isAbsenceAnswered = (role: ProfileType): boolean => {
    const answer = absenceValues[role.id ?? -1];
    return answer === ABSENCE_YES || answer === ABSENCE_NO;
  };

  const handleNext = (): void => {
    const hasError =
      visibleAbsenceRoles.some((role) => !isAbsenceAnswered(role)) ||
      visibleRoles.some((role) => errorForRole(role) !== null);
    if (hasError) {
      setShowErrors(true);
      return;
    }
    onCompleted(
      visibleRoles.map((role) => ({
        study_role_id: role.id as number,
        count: Number((values[role.id ?? -1] ?? "").trim()),
      })),
      visibleAbsenceRoles.map((role) => ({
        study_role_id: role.id as number,
        is_absent: absenceValues[role.id ?? -1] === absentAnswerOf(role),
      }))
    );
  };

  return (
    <Container>
      <Content>
        <Title>{t("home_family_title")}</Title>
        {visibleAbsenceRoles.map((role) => (
          <div key={`abs-${role.id}`}>
            <Question
              question={buildAbsenceQuestion(
                role,
                t("home_family_absence_fallback", { role: role.profile }),
                t("common_yes"),
                t("common_no")
              )}
              answer={absenceValues[role.id ?? -1] ?? null}
              onAnswerChange={(value) =>
                setAbsenceValues((current) => ({
                  ...current,
                  // AnswerRadio emits NaN on a re-tap (deselect): anything but 1/2 = unanswered.
                  [role.id ?? -1]:
                    typeof value === "number" &&
                    (value === ABSENCE_YES || value === ABSENCE_NO)
                      ? value
                      : undefined,
                }))
              }
              readText={readText}
              cardMargin="12px 20px"
            />
            {showErrors && !isAbsenceAnswered(role) && (
              <CompositionFieldError>{t("form_error_required")}</CompositionFieldError>
            )}
          </div>
        ))}
        {visibleRoles.map((role) => (
          <div key={`count-${role.id}`}>
            <Question
              question={buildCountQuestion(
                role,
                t("home_family_count_fallback", { role: role.profile })
              )}
              answer={values[role.id ?? -1] ?? ""}
              onAnswerChange={(value) =>
                setValues((current) => ({
                  ...current,
                  [role.id ?? -1]: String(value ?? ""),
                }))
              }
              onEnterNext={handleNext}
              readText={readText}
              cardMargin="12px 20px"
            />
            {showErrors && errorForRole(role) && (
              <CompositionFieldError>{errorForRole(role)}</CompositionFieldError>
            )}
          </div>
        ))}
      </Content>

      <NextPrevButtons onPreviousClick={goPrevious} onNextClick={handleNext} />
    </Container>
  );
};

const Content = styled.div`
  display: flex;
  flex-direction: column;
`;

const Title = styled.h1`
  margin: 18px 50px 4px;
  font-size: 20px;
  font-weight: bold;
  color: var(--app-color-text, #393939);
`;

const CompositionFieldError = styled(FieldError)`
  margin: 6px 25px 0;
`;

export default DisplayFamilyComposition;
